import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ActionIcon, Tooltip, Portal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { 
    IconSparkles, 
    IconMicrophone, 
    IconSend, 
    IconX, 
    IconTrash,
    IconArrowRight,
    IconBulb,
    IconArrowsMaximize,
    IconArrowsMinimize,
    IconBolt,
    IconClock,
    IconChartBar,
    IconCalendarStats,
    IconExternalLink
} from '@tabler/icons-react';
import { useAsistenteIA } from '../../../globales/contextos/asistenteIAContext';
import { useAutenticacion } from '../../../globales/contextos/authContext';
import classes from './asistenteIA.module.css';

// Declaración para el API de reconocimiento de voz del navegador
const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

// Función utilitaria para parsear markdown simple (negritas y cursivas) sin dependencias
const parsearMarkdown = (texto: string): React.ReactNode => {
    if (!texto) return '';

    // Dividir el texto en líneas
    const lineas = texto.split('\n');
    const elementos: React.ReactNode[] = [];
    let listaActual: React.ReactNode[] = [];
    let enLista = false;

    const procesarEstilosLinea = (txt: string): React.ReactNode[] => {
        let partes: React.ReactNode[] = [];
        let buffer = "";
        let i = 0;
        while (i < txt.length) {
            if (txt.startsWith('**', i)) {
                if (buffer) {
                    partes.push(buffer);
                    buffer = "";
                }
                const fin = txt.indexOf('**', i + 2);
                if (fin !== -1) {
                    partes.push(<strong key={i} style={{ fontWeight: 700 }}>{txt.substring(i + 2, fin)}</strong>);
                    i = fin + 2;
                } else {
                    buffer += '**';
                    i += 2;
                }
            } else if (txt.startsWith('*', i)) {
                if (buffer) {
                    partes.push(buffer);
                    buffer = "";
                }
                const fin = txt.indexOf('*', i + 1);
                if (fin !== -1) {
                    partes.push(<em key={i}>{txt.substring(i + 1, fin)}</em>);
                    i = fin + 1;
                } else {
                    buffer += '*';
                    i += 1;
                }
            } else if (txt.startsWith('`', i)) {
                if (buffer) {
                    partes.push(buffer);
                    buffer = "";
                }
                const fin = txt.indexOf('`', i + 1);
                if (fin !== -1) {
                    partes.push(
                        <code key={i} style={{ 
                            backgroundColor: '#f1f5f9', 
                            padding: '2px 4px', 
                            borderRadius: '4px', 
                            fontFamily: 'monospace', 
                            fontSize: '0.9em',
                            color: '#0f172a'
                        }}>
                            {txt.substring(i + 1, fin)}
                        </code>
                    );
                    i = fin + 1;
                } else {
                    buffer += '`';
                    i += 1;
                }
            } else {
                buffer += txt[i];
                i++;
            }
        }
        if (buffer) {
            partes.push(buffer);
        }
        return partes;
    };

    for (let l = 0; l < lineas.length; l++) {
        const linea = lineas[l].trim();

        if (linea === '') {
            if (enLista) {
                elementos.push(<ul key={`ul-${l}`} style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>{listaActual}</ul>);
                listaActual = [];
                enLista = false;
            }
            continue;
        }

        const matchLista = linea.match(/^[\-\*\•]\s+(.*)$/);
        if (matchLista) {
            enLista = true;
            listaActual.push(<li key={`li-${l}`} style={{ marginBottom: '4px' }}>{procesarEstilosLinea(matchLista[1])}</li>);
        } else {
            if (enLista) {
                elementos.push(<ul key={`ul-${l}`} style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>{listaActual}</ul>);
                listaActual = [];
                enLista = false;
            }

            const matchHeader = linea.match(/^(#{1,6})\s+(.*)$/);
            if (matchHeader) {
                const nivel = matchHeader[1].length;
                const tituloTexto = matchHeader[2];
                const fontSize = nivel === 1 ? '1.4em' : nivel === 2 ? '1.2em' : '1.1em';
                elementos.push(
                    <div key={`h-${l}`} style={{ 
                        fontWeight: 700, 
                        fontSize: fontSize, 
                        marginTop: '12px', 
                        marginBottom: '6px',
                        color: '#1e3a8a'
                    }}>
                        {procesarEstilosLinea(tituloTexto)}
                    </div>
                );
            } else if (linea.startsWith('|') && linea.endsWith('|')) {
                const celdas = linea.split('|').slice(1, -1).map(c => c.trim());
                if (celdas.every(c => /^:\?-+:?$/.test(c) || /^-+$/.test(c))) {
                    continue;
                }
                elementos.push(
                    <div key={`tr-${l}`} style={{ 
                        display: 'flex', 
                        borderBottom: '1px solid #e2e8f0', 
                        padding: '6px 0',
                        fontSize: '0.95em'
                    }}>
                        {celdas.map((celda, cIndex) => (
                            <div key={`td-${cIndex}`} style={{ flex: 1, padding: '0 8px' }}>
                                {procesarEstilosLinea(celda)}
                            </div>
                        ))}
                    </div>
                );
            } else {
                elementos.push(
                    <p key={`p-${l}`} style={{ margin: '6px 0', lineHeight: 1.5 }}>
                        {procesarEstilosLinea(linea)}
                    </p>
                );
            }
        }
    }

    if (enLista && listaActual.length > 0) {
        elementos.push(<ul key="ul-final" style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>{listaActual}</ul>);
    }

    return <div style={{ display: 'flex', flexDirection: 'column' }}>{elementos}</div>;
};

const normalizarHtml = (htmlOriginal: string): string => {
    if (!htmlOriginal) return "";
    
    // Nuestro CSS simplificado para el drawer de chat
    const cssDrawer = `
        <style>
            html, body {
                background-color: transparent !important;
                background: transparent !important;
                margin: 0 !important;
                padding: 0 !important;
                font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
                overflow: hidden !important;
                scrollbar-width: none !important;
            }
            html::-webkit-scrollbar, body::-webkit-scrollbar {
                display: none !important;
            }
            .container {
                max-width: 700px !important;
                padding: 0 !important;
                box-shadow: none !important;
                background: transparent !important;
                border-radius: 0 !important;
                margin: 0 auto !important;
                width: 100% !important;
                border: none !important;
            }
            h2 {
                font-size: 14.5px !important;
                margin-top: 4px !important;
                margin-bottom: 12px !important;
                color: #1e293b !important;
                font-weight: 600 !important;
                text-align: center !important;
            }
            .chart-wrapper, canvas {
                max-width: 100% !important;
                width: 100% !important;
                height: 220px !important;
                max-height: 220px !important;
            }
            @media (min-width: 600px) {
                .chart-wrapper, canvas {
                    height: 320px !important;
                    max-height: 320px !important;
                }
            }
        </style>
    `;

    // Reemplazar todos los bloques <style>...</style> originales por nuestro CSS simplificado
    const regexStyle = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
    let htmlModificado = htmlOriginal;
    
    if (regexStyle.test(htmlOriginal)) {
        htmlModificado = htmlOriginal.replace(regexStyle, cssDrawer);
    } else {
        const regexHead = /<head\s*>/i;
        if (regexHead.test(htmlModificado)) {
            htmlModificado = htmlModificado.replace(regexHead, `<head>${cssDrawer}`);
        } else {
            htmlModificado = cssDrawer + htmlModificado;
        }
    }

    // Inyectar el script interceptor de Chart y el script de redimensionamiento
    const scriptInyectado = `
        <script>
            (function() {
                var _Chart;
                Object.defineProperty(window, 'Chart', {
                    configurable: true,
                    enumerable: true,
                    get: function() {
                        return _Chart;
                    },
                    set: function(val) {
                        _Chart = val;
                        if (val && val.defaults) {
                            val.defaults.maintainAspectRatio = false;
                            if (val.defaults.global) {
                                val.defaults.global.maintainAspectRatio = false;
                            }
                        }
                    }
                });
            })();
            
            function enviarAltura() {
                const height = document.body.scrollHeight || document.documentElement.scrollHeight;
                window.parent.postMessage({
                    tipo: 'conekt-iframe-resize',
                    altura: height
                }, '*');
            }
            window.addEventListener('load', enviarAltura);
            window.addEventListener('resize', enviarAltura);
            if (window.ResizeObserver) {
                const observer = new ResizeObserver(enviarAltura);
                observer.observe(document.body);
            }
            setTimeout(enviarAltura, 100);
            setTimeout(enviarAltura, 300);
            setTimeout(enviarAltura, 600);
            setTimeout(enviarAltura, 1200);
        </script>
    `;

    // Inyectar script al inicio del body (o al inicio del documento si no hay body)
    const regexBody = /<body\s*[^>]*>/i;
    if (regexBody.test(htmlModificado)) {
        htmlModificado = htmlModificado.replace(regexBody, (match) => `${match}${scriptInyectado}`);
    } else {
        htmlModificado = scriptInyectado + htmlModificado;
    }

    return htmlModificado;
};

const VisualizadorHtml: React.FC<{ html: string }> = ({ html }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [altura, setAltura] = useState(300);

    const abrirEnNuevaVentana = () => {
        try {
            const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (e) {
            const nuevaVentana = window.open();
            if (nuevaVentana) {
                nuevaVentana.document.open();
                nuevaVentana.document.write(html);
                nuevaVentana.document.close();
            }
        }
    };

    useEffect(() => {
        const manejarMensaje = (e: MessageEvent) => {
            if (e.data && e.data.tipo === 'conekt-iframe-resize') {
                if (iframeRef.current && iframeRef.current.contentWindow === e.source) {
                    const nuevaAltura = Math.min(Math.max(e.data.altura, 150), 600);
                    setAltura(nuevaAltura);
                }
            }
        };
        window.addEventListener('message', manejarMensaje);
        return () => window.removeEventListener('message', manejarMensaje);
    }, []);

    const htmlNormalizado = useMemo(() => normalizarHtml(html), [html]);

    return (
        <div style={{
            marginTop: '12px',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
            width: '100%'
        }}>
            {/* Header muy compacto */}
            <div style={{
                padding: '6px 12px',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '11px',
                color: '#64748b',
                fontWeight: 600
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <IconChartBar size={14} style={{ color: '#2563eb' }} />
                    <span>Visualización</span>
                </div>
                <Tooltip label="Abrir en pestaña nueva" position="left" withinPortal zIndex={10000}>
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={abrirEnNuevaVentana}
                        aria-label="Abrir en pestaña nueva"
                        style={{ 
                            width: '22px', 
                            height: '22px',
                            color: '#2563eb'
                        }}
                    >
                        <IconExternalLink size={14} style={{ strokeWidth: 2.5 }} />
                    </ActionIcon>
                </Tooltip>
            </div>
            
            {/* Contenedor del iframe con padding mínimo */}
            <div style={{ padding: '8px', backgroundColor: '#ffffff' }}>
                <iframe
                    key={htmlNormalizado.slice(0, 50) + htmlNormalizado.length}
                    ref={iframeRef}
                    srcDoc={htmlNormalizado}
                    title="Visualización de datos"
                    sandbox="allow-scripts"
                    style={{
                        width: '100%',
                        height: `${altura}px`,
                        border: 'none',
                        display: 'block',
                        transition: 'height 0.2s ease',
                        backgroundColor: 'transparent'
                    }}
                />
            </div>
        </div>
    );
};

export const AsistenteIADrawer: React.FC = () => {
    const { 
        drawerAbierto, 
        cargandoRespuesta, 
        historialMensajes, 
        cerrarDrawer, 
        enviarMensaje,
        limpiarConversacion,
        sesionCreada
    } = useAsistenteIA();

    const { usuario } = useAutenticacion();
    const [inputTexto, setInputTexto] = useState<string>('');
    const [escuchando, setEscuchando] = useState<boolean>(false);
    const [soportaVoz, setSoportaVoz] = useState<boolean>(false);

    // Estados para redimensionar y pantalla completa
    const [ancho, setAncho] = useState<number>(320);
    const [pantallaCompleta, setPantallaCompleta] = useState<boolean>(false);
    const [estaRedimensionando, setEstaRedimensionando] = useState<boolean>(false);

    // Cargar ancho cacheado desde localStorage al montar
    useEffect(() => {
        if (usuario) {
            const userSuffix = usuario.Email || String(usuario.Id) || 'default';
            const cachedWidth = localStorage.getItem(`conekt_chat_width_${userSuffix}`);
            if (cachedWidth) {
                setAncho(parseInt(cachedWidth, 10));
            }
        }
    }, [usuario]);

    // Guardar el ancho personalizado en localStorage al cambiar
    useEffect(() => {
        if (usuario && ancho !== 320) {
            const userSuffix = usuario.Email || String(usuario.Id) || 'default';
            localStorage.setItem(`conekt_chat_width_${userSuffix}`, ancho.toString());
        }
    }, [ancho, usuario]);

    const bodyRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const recognitionRef = useRef<any>(null);

    // Hook de Mantine para responsividad
    const esMovil = useMediaQuery('(max-width: 768px)');

    // Obtener el primer nombre dividiendo por el espacio
    const primerNombre = usuario?.NombreCompleto 
        ? usuario.NombreCompleto.split(' ')[0] 
        : 'Usuario';

    // Chips de sugerencia para los usuarios basados exactamente en el Mock
    const atajosInteligentes = [
        {
            texto: "¿Emergencias activas?",
            titulo: "Emergencias",
            descripcion: "Alertas y criticidad del sistema",
            icono: <IconBolt size={18} style={{ color: '#e11d48' }} />, // Rojo/Coral
            bgIcono: '#fef2f2',
            conFlecha: false,
        },
        {
            texto: "¿Tareas en curso?",
            titulo: "Tareas en curso",
            descripcion: "Monitoreo en tiempo real",
            icono: <IconClock size={18} style={{ color: '#2563eb' }} />, // Azul
            bgIcono: '#eff6ff',
            conFlecha: true,
        },
        {
            texto: "Resumen del estado operativo",
            titulo: "Operación",
            descripcion: "Resumen del estado operativo",
            icono: <IconChartBar size={18} style={{ color: '#475569' }} />, // Gris/Slate
            bgIcono: '#f1f5f9',
            conFlecha: false,
        },
        {
            texto: "Mantenimientos pendientes",
            titulo: "Mantenimiento",
            descripcion: "Órdenes de trabajo y revisiones",
            icono: <IconCalendarStats size={18} style={{ color: '#ea580c' }} />, // Naranja
            bgIcono: '#fff7ed',
            conFlecha: false,
        }
    ];

    // Verificar soporte de Web Speech API
    useEffect(() => {
        if (SpeechRecognition) {
            setSoportaVoz(true);
            const recognition = new SpeechRecognition();
            recognition.continuous = false;
            recognition.lang = 'es-ES';
            recognition.interimResults = false;
            recognition.maxAlternatives = 1;

            recognition.onstart = () => {
                setEscuchando(true);
            };

            recognition.onend = () => {
                setEscuchando(false);
            };

            recognition.onresult = (event: any) => {
                const transcript = event.results[0][0].transcript;
                setInputTexto((prev) => {
                    const separator = prev.trim() ? ' ' : '';
                    const nuevoTexto = prev + separator + transcript;
                    return nuevoTexto.slice(0, limiteCaracteres);
                });
                inputRef.current?.focus();
            };

            recognition.onerror = (event: any) => {
                console.error("Error en Speech Recognition:", event.error);
                setEscuchando(false);
            };

            recognitionRef.current = recognition;
        }
    }, []);

    // Manejar el redimensionamiento por arrastre
    useEffect(() => {
        const manejarMouseMove = (e: MouseEvent) => {
            if (!estaRedimensionando) return;
            const nuevoAncho = window.innerWidth - e.clientX;
            
            // Límites: mínimo 320px y máximo 80% del ancho de la ventana
            const limiteMaximo = window.innerWidth * 0.8;
            if (nuevoAncho >= 320 && nuevoAncho <= limiteMaximo) {
                setAncho(nuevoAncho);
            }
        };

        const manejarMouseUp = () => {
            setEstaRedimensionando(false);
        };

        if (estaRedimensionando) {
            window.addEventListener('mousemove', manejarMouseMove);
            window.addEventListener('mouseup', manejarMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', manejarMouseMove);
            window.removeEventListener('mouseup', manejarMouseUp);
        };
    }, [estaRedimensionando]);

    // Escuchar tecla Escape para cerrar el drawer
    useEffect(() => {
        const manejarTeclaEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && drawerAbierto) {
                cerrarDrawer();
            }
        };

        window.addEventListener('keydown', manejarTeclaEscape);
        return () => {
            window.removeEventListener('keydown', manejarTeclaEscape);
        };
    }, [drawerAbierto, cerrarDrawer]);

    // Enfocar el input cuando se abre el drawer
    useEffect(() => {
        if (drawerAbierto) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [drawerAbierto]);

    // Hacer scroll automático al mensaje más reciente
    useEffect(() => {
        if (bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [historialMensajes, cargandoRespuesta]);

    // Auto-ajustar la altura del textarea según su contenido (con un límite máximo de 120px)
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto'; // Resetear
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
        }
    }, [inputTexto]);

    // Sincronizar el ancho del chat y la transición con variables CSS globales en el root
    useEffect(() => {
        const width = drawerAbierto ? (pantallaCompleta ? '100%' : `${ancho}px`) : '0px';
        document.documentElement.style.setProperty('--chat-width', width);
        return () => {
            document.documentElement.style.setProperty('--chat-width', '0px');
        };
    }, [drawerAbierto, pantallaCompleta, ancho]);

    // Desactivar pantalla completa si cambia a tamaño móvil para evitar inconsistencias
    useEffect(() => {
        if (esMovil) {
            setPantallaCompleta(false);
        }
    }, [esMovil]);

    useEffect(() => {
        const transition = estaRedimensionando 
            ? 'none' 
            : 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        document.documentElement.style.setProperty('--chat-transition', transition);
    }, [estaRedimensionando]);

    // Alternar grabación por voz
    const alternarGrabacion = () => {
        if (!soportaVoz || !recognitionRef.current) return;

        if (escuchando) {
            recognitionRef.current.stop();
        } else {
            try {
                recognitionRef.current.start();
            } catch (err) {
                console.error("Error al iniciar grabación:", err);
            }
        }
    };

    // Manejar envío de mensaje
    const manejarEnviar = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!inputTexto.trim() || cargandoRespuesta) return;

        const texto = inputTexto;
        setInputTexto(''); // Limpiar input
        await enviarMensaje(texto);
    };

    // Manejar enter en el textarea (Shift + Enter para salto de línea, Enter para enviar)
    const manejarKeyDownTextarea = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            manejarEnviar();
        }
    };

    // Al hacer clic en un chip de sugerencia, se copia al input
    const seleccionarSugerencia = (sugerencia: string) => {
        setInputTexto(sugerencia);
        inputRef.current?.focus();
    };

    // Formatear hora para los mensajes
    const formatearHora = (fecha: Date) => {
        return fecha.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Contador de caracteres
    const limiteCaracteres = 500;
    const caracteresRestantes = limiteCaracteres - inputTexto.length;
    const esCritico = caracteresRestantes <= 100;

    const iniciarArrastre = (e: React.MouseEvent) => {
        e.preventDefault();
        setEstaRedimensionando(true);
    };

    // Determinar estilos CSS inline para el contenedor flotante montado en Portal
    const obtenerEstilosVisualesDrawer = () => {
        if (esMovil) {
            return {
                position: 'fixed' as const,
                top: 0,
                right: drawerAbierto ? 0 : '-100%',
                width: '100%',
                height: '100vh',
                zIndex: 9999, // Muy alto para sobreponerse a modales en móvil
                borderTop: 'none',
                visibility: (drawerAbierto || estaRedimensionando) ? 'visible' as const : 'hidden' as const,
                transition: drawerAbierto
                    ? 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s 0.3s'
            };
        }

        return {
            position: 'fixed' as const,
            top: '102px', // MenuBarra (56px) + MenuModulos (46px)
            right: drawerAbierto ? 0 : `-${ancho}px`,
            width: pantallaCompleta ? '100%' : `${ancho}px`,
            height: 'calc(100vh - 102px)',
            zIndex: 9999, // Muy alto para sobreponerse a modales y backdrops en desktop
            visibility: (drawerAbierto || estaRedimensionando) ? 'visible' as const : 'hidden' as const,
            transition: estaRedimensionando 
                ? 'none' 
                : drawerAbierto
                    ? 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s 0.3s'
        };
    };

    return (
        <>
            {/* 1. Espaciador estático en el flujo flex de la app (solo en desktop) para encoger el main-content */}
            {!esMovil && !pantallaCompleta && (
                <div 
                    style={{
                        width: drawerAbierto ? `${ancho}px` : '0px',
                        flexShrink: 0,
                        transition: estaRedimensionando ? 'none' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        overflow: 'hidden'
                    }}
                />
            )}

            {/* 2. Chat Drawer renderizado mediante Portal en la raíz del body para sobreponerse a los modales */}
            <Portal>
                <div 
                    className={`${classes.drawer} ${drawerAbierto ? classes.open : ''}`} 
                    style={obtenerEstilosVisualesDrawer()}
                    aria-hidden={!drawerAbierto}
                >
                        {/* Control de redimensionamiento (drag handle) en el borde izquierdo (solo en desktop) */}
                        {!esMovil && (
                            <div 
                                className={classes.resizeHandle} 
                                onMouseDown={iniciarArrastre}
                                title="Arrastra para cambiar el tamaño"
                            />
                        )}

                        {/* Header del Drawer (Fondo blanco, letras e ícono en azul) */}
                        <div className={classes.header}>
                            <div className={classes.headerTitleContainer}>
                                <div className={classes.headerTitle}>
                                    <IconSparkles size={18} />
                                    <span>Asistente de IA</span>
                                </div>
                                {sesionCreada && (
                                    <div className={classes.onlineBadge}>
                                        <span className={classes.onlineDot} />
                                        <span>EN LÍNEA</span>
                                    </div>
                                )}
                            </div>
                            <div className={classes.headerActions}>
                                {historialMensajes.length > 0 && (
                                    <Tooltip label="Limpiar chat" position="bottom" withinPortal zIndex={10000}>
                                        <ActionIcon
                                            variant="subtle"
                                            color="primary"
                                            radius="xl"
                                            size="md"
                                            onClick={limpiarConversacion}
                                            aria-label="Limpiar historial de chat"
                                        >
                                            <IconTrash size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                                {!esMovil && (
                                    <Tooltip label={pantallaCompleta ? "Restaurar tamaño" : "Pantalla completa"} position="bottom" withinPortal zIndex={10000}>
                                        <ActionIcon
                                            variant="subtle"
                                            color="primary"
                                            radius="xl"
                                            size="md"
                                            onClick={() => setPantallaCompleta(!pantallaCompleta)}
                                            aria-label="Alternar pantalla completa"
                                        >
                                            {pantallaCompleta ? <IconArrowsMinimize size={18} /> : <IconArrowsMaximize size={18} />}
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                                <Tooltip label="Cerrar" position="bottom" withinPortal zIndex={10000}>
                                    <ActionIcon
                                        variant="subtle"
                                        color="primary"
                                        radius="xl"
                                        size="md"
                                        onClick={cerrarDrawer}
                                        aria-label="Cerrar panel de asistente"
                                    >
                                        <IconX size={18} />
                                    </ActionIcon>
                                </Tooltip>
                            </div>
                        </div>

                        {/* Cuerpo de la Conversación */}
                        <div className={classes.body} ref={bodyRef}>
                            {historialMensajes.length === 0 ? (
                                /* Pantalla de Bienvenida al estilo Mock */
                                <div className={classes.welcomeContainer}>
                                    <div className={classes.welcomeIconWrapper}>
                                        <IconBulb size={30} />
                                    </div>
                                    <h2 className={classes.welcomeTitle}>
                                        Hola, {primerNombre}.
                                        <span className={classes.welcomeTitleBlue}>¿En qué puedo ayudarte hoy?</span>
                                    </h2>
                                    <p className={classes.welcomeDesc}>
                                        Consulta estados de operación, tareas activas y mantenimientos en tiempo real.
                                    </p>
                                    
                                    <div className={classes.suggestionsTitle}>Atajos Inteligentes</div>
                                    <div className={`${classes.suggestionsContainer} ${pantallaCompleta ? classes.gridMode : ''}`}>
                                        {atajosInteligentes.map((atajo, index) => (
                                            <button 
                                                key={index} 
                                                className={classes.chip}
                                                onClick={() => seleccionarSugerencia(atajo.texto)}
                                                type="button"
                                            >
                                                <div className={classes.chipContent}>
                                                    <div 
                                                        className={classes.chipIcon}
                                                        style={{ 
                                                            backgroundColor: (pantallaCompleta && !esMovil) ? atajo.bgIcono : 'transparent' 
                                                        }}
                                                    >
                                                        {atajo.icono}
                                                    </div>
                                                    <div className={classes.chipTextContainer}>
                                                        <span className={classes.chipTitleDefault}>{atajo.texto}</span>
                                                        <span className={classes.chipTitleGrid}>{atajo.titulo}</span>
                                                        <span className={classes.chipDesc}>{atajo.descripcion}</span>
                                                    </div>
                                                </div>
                                                {!(pantallaCompleta && !esMovil) && atajo.conFlecha && (
                                                    <div className={classes.chipArrow}>
                                                        <IconArrowRight size={16} />
                                                    </div>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                /* Burbujas de Conversación */
                                <div className={classes.messagesList}>
                                    {historialMensajes.map((msg) => (
                                        <div 
                                            key={msg.id} 
                                            className={`${classes.messageRow} ${msg.remitente === 'usuario' ? classes.user : classes.agent}`}
                                            style={{ flexDirection: 'column', alignItems: msg.remitente === 'usuario' ? 'flex-end' : 'flex-start' }}
                                        >
                                            <div className={classes.bubble}>
                                                <div style={{ whiteSpace: 'pre-wrap' }}>{parsearMarkdown(msg.texto)}</div>
                                                <span className={classes.bubbleTime}>
                                                    {formatearHora(msg.fecha)}
                                                </span>
                                            </div>
                                            {msg.html && (
                                                <VisualizadorHtml html={msg.html} />
                                            )}
                                        </div>
                                    ))}

                                    {/* Indicador de Escritura */}
                                    {cargandoRespuesta && (
                                        <div className={`${classes.messageRow} ${classes.agent}`}>
                                            <div className={classes.bubble}>
                                                <div className={classes.typingContainer} aria-label="El asistente está escribiendo">
                                                    <div className={classes.typingDot}></div>
                                                    <div className={classes.typingDot}></div>
                                                    <div className={classes.typingDot}></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Footer de Entrada de Texto estilo Card */}
                        <form className={classes.footer} onSubmit={manejarEnviar}>
                            <div className={classes.inputCard}>
                                <textarea
                                    ref={inputRef}
                                    className={classes.inputField}
                                    placeholder={escuchando ? "Escuchando voz..." : "Escribe tu consulta aquí..."}
                                    value={inputTexto}
                                    onChange={(e) => setInputTexto(e.target.value.slice(0, limiteCaracteres))}
                                    onKeyDown={manejarKeyDownTextarea}
                                    disabled={cargandoRespuesta}
                                    maxLength={limiteCaracteres}
                                    rows={1}
                                    aria-label="Mensaje para el asistente"
                                />

                                <div className={classes.bottomRow}>
                                    <div className={classes.leftActions}>
                                        {soportaVoz && (
                                            <Tooltip label={escuchando ? "Detener grabación" : "Grabar consulta (voz a texto)"} withinPortal zIndex={10000}>
                                                <ActionIcon
                                                    variant={escuchando ? 'filled' : 'subtle'}
                                                    color={escuchando ? 'red' : 'gray'}
                                                    radius="xl"
                                                    size="md"
                                                    onClick={alternarGrabacion}
                                                    disabled={cargandoRespuesta}
                                                    className={escuchando ? classes.micButtonActive : classes.micButton}
                                                    style={escuchando ? { 
                                                        color: '#ffffff',
                                                        backgroundColor: '#f43f5e'
                                                    } : undefined}
                                                    aria-label={escuchando ? "Detener reconocimiento de voz" : "Iniciar reconocimiento de voz"}
                                                >
                                                    <IconMicrophone size={18} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                        <span className={`${classes.counter} ${esCritico ? classes.counterWarning : ''}`}>
                                            {inputTexto.length} / {limiteCaracteres}
                                        </span>
                                    </div>

                                    <Tooltip label="Enviar consulta" withinPortal zIndex={10000}>
                                        <ActionIcon
                                            type="submit"
                                            className={classes.sendButton}
                                            disabled={!inputTexto.trim() || cargandoRespuesta}
                                            aria-label="Enviar mensaje"
                                        >
                                            <IconSend size={18} />
                                        </ActionIcon>
                                    </Tooltip>
                                </div>
                            </div>
                        </form>
                    </div>
                </Portal>
        </>
    );
};

export default AsistenteIADrawer;
